import React, { FormEvent, useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Button } from 'primereact/button'

import { isPasswordLongEnough } from '../../../common/domain/account-rules'
import { Header } from '../../../lib/header'
import { CurrentUser } from '../../../lib/currentUser'
import usePatch from '../../../common/hooks/usePatch'
import { UserProfileChangePassword } from '../user-profile-change-password'
import { Alert } from '../../../lib/alert'
import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import { setUser } from '../../../common/redux/features/user/userSlice'
import { isFetchBaseQueryError } from '../../../common/redux/api/fetchBaseQueryWithReauth'
import { useUserUpdateLoggedInUserMutation } from '../../../common/redux/api/web-api'

const SContainer = styled.div`
    width: 100%;
`

const SForm = styled.form`
    display: flex;
    gap: 20px;
    flex-direction: column;
`

const SRow = styled.div`
    display: flex;
    gap: 12px;

    &.alignSelf {
        align-self: flex-end;
    }

    @media (max-width: 500px) {
        flex-direction: column;
    }
`

const SWrapper = styled.div<{ direction: string }>`
    display: flex;
    gap: 12px;
    flex-direction: ${({ direction }) => direction};
    justify-content: ${({ direction }) => (direction === 'row' ? 'space-between' : 'center')};
`

const SInput = styled.input`
    width: 175px;
    height: 30px;
    padding: 5px 10px;
    font-size: 12px;
    line-height: 1.5;
    border-radius: 3px;

    @media (max-width: 500px) {
        width: 100%;
    }
`

export type UserProfileProps = {
    user: CurrentUser
    className?: ClassName
    style?: React.CSSProperties
}

export function UserProfile({ user, className, style }: UserProfileProps): React.ReactElement {
    const text = useText()
    const dispatch = useDispatch()
    const { value: userDetails, apply: changeDetails } = usePatch(user)
    const [updateUser, { isLoading: isUpdating }] = useUserUpdateLoggedInUserMutation()
    const [openPasswordChange, setOpenPasswordChange] = useState(false)
    const [emptyFields, setEmptyFields] = useState<Array<{ message: string; field: string }>>([])
    const [updateMessage, setUpdateMessage] = useState<{ message: string | null; type: 'Error' | 'Success' | null }>({
        message: null,
        type: null,
    })
    const [passwordErrors, setPasswordErrors] = useState<string>('')

    const getUpdateErrorMessage = (error: unknown) => {
        if (
            isFetchBaseQueryError(error) &&
            error.data &&
            typeof error.data === 'object' &&
            'messageCode' in error.data &&
            typeof error.data.messageCode === 'string'
        ) {
            return text(error.data.messageCode)
        }

        return text('msgProfileNotUpdated')
    }

    const handlePasswordChangeVisibility = (isOpen: boolean) => {
        setOpenPasswordChange(isOpen)
        if (!isOpen) changeDetails({ passwordChange: null })
    }

    /** Update field **/
    const updateField = (field: string, value: string) => {
        changeDetails({ [field]: value })
        const missingFields: Array<{ message: string; field: string }> = [
            ...emptyFields.filter((m) => m.field !== field),
        ]
        if (field === 'firstName' && !value.length) {
            missingFields.push({ message: text('msgFirstNameRequired'), field: 'firstName' })
        }
        if (field === 'lastName' && !value.length) {
            missingFields.push({ message: text('msgLastNameRequired'), field: 'lastName' })
        }
        if (field === 'mobile' && !value.length) {
            missingFields.push({ message: text('msgMobileRequired'), field: 'mobile' })
        }
        setEmptyFields(missingFields)

        if (updateMessage.type) setUpdateMessage({ type: null, message: null })
    }

    /** Check if new password is too short **/
    useEffect(() => {
        if (userDetails.passwordChange?.newPassword && !isPasswordLongEnough(userDetails.passwordChange.newPassword)) {
            setPasswordErrors(text('msgPasswordToShort'))
        } else {
            setPasswordErrors('')
        }
    }, [text, userDetails.passwordChange])
    /***************************************/

    /** Check password before sending request **/
    const checkPasswordChange = (passwordChange: CurrentUser['passwordChange']) => {
        const currentPassword = passwordChange?.currentPassword ?? ''
        const newPassword = passwordChange?.newPassword ?? ''
        const passwordCompare = passwordChange?.passwordCompare ?? ''

        if (!currentPassword.length) {
            setPasswordErrors(text('lblCurrentPasswordEmptyField'))
            return true
        }

        if (!isPasswordLongEnough(newPassword)) {
            setPasswordErrors(text('msgPasswordToShort'))
            return true
        }

        if (newPassword !== passwordCompare) {
            setPasswordErrors(text('msgPasswordFailedToUpdate'))
            return true
        }
        return false
    }
    /*******************************************/

    /** Handle submit **/
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setUpdateMessage({ message: null, type: null })
        if (openPasswordChange && checkPasswordChange(userDetails.passwordChange)) return

        try {
            await updateUser({ chargePointUser: userDetails }).unwrap()
            setUpdateMessage({
                message: openPasswordChange ? text('msgProfilePasswordUpdated') : text('msgProfileUpdated'),
                type: 'Success',
            })
            dispatch(setUser(userDetails))
        } catch (error) {
            setUpdateMessage({ message: getUpdateErrorMessage(error), type: 'Error' })
        }
    }
    /*******************/
    const widgetTitle = (<Header code="lblMyProfileWidget" className="myProfile" />) as unknown as string

    return (
        <SContainer className={cn(className)} style={style}>
            <WidgetLayout title={widgetTitle} data-testid="myProfile">
                {emptyFields?.map((field, index) => (
                    <Alert key={index} message={field.message} className={`${field.field}Error`} error />
                ))}
                {passwordErrors.length > 0 && <Alert message={passwordErrors} error className="passwordError" />}
                {updateMessage.message && (
                    <Alert
                        message={updateMessage.message}
                        error={updateMessage.type === 'Error'}
                        className={
                            updateMessage.message.includes('password is incorrect')
                                ? 'passwordError'
                                : `update${updateMessage.type}`
                        }
                    />
                )}
                <SForm onSubmit={handleSubmit}>
                    <SRow>
                        <SInput
                            className="form-control"
                            placeholder={text('lblFirstName')}
                            type="text"
                            name="firstName"
                            onChange={(e) => updateField('firstName', e.target.value)}
                            value={userDetails.firstName}
                            id="firstName"
                            maxLength={100}
                            required
                        />
                        <SInput
                            className="form-control"
                            placeholder={text('lblLastNameField')}
                            type="text"
                            name="lastName"
                            value={userDetails.lastName}
                            onChange={(e) => updateField('lastName', e.target.value)}
                            id="lastName"
                            maxLength={100}
                            required
                        />
                    </SRow>
                    <SRow>
                        <SInput
                            className="form-control"
                            placeholder={text('lblEmailAddressField')}
                            type="email"
                            name="email"
                            value={user.email}
                            required
                            readOnly
                        />
                        <SInput
                            className="form-control"
                            placeholder={text('lblMobilePhoneField')}
                            type="text"
                            name="mobile"
                            value={userDetails?.mobile}
                            onChange={(e) => updateField('mobile', e.target.value)}
                            id="mobile"
                            required
                        />
                    </SRow>
                    <SWrapper direction={openPasswordChange ? 'column' : 'row'}>
                        <UserProfileChangePassword
                            user={userDetails}
                            onChange={changeDetails}
                            openPasswordChange={openPasswordChange}
                            setOpenPasswordChange={handlePasswordChangeVisibility}
                        />
                        <SRow className={cn({ alignSelf: openPasswordChange })}>
                            {openPasswordChange && (
                                <Button
                                    type="button"
                                    label={text('btnCancel')}
                                    text
                                    onClick={() => handlePasswordChangeVisibility(false)}
                                />
                            )}
                            <Button
                                type="submit"
                                label={text('btnSave')}
                                icon="pi pi-save"
                                disabled={emptyFields.length > 0 || passwordErrors.length > 0}
                                loading={isUpdating}
                            />
                        </SRow>
                    </SWrapper>
                </SForm>
            </WidgetLayout>
        </SContainer>
    )
}
